/**
 * ChatComposer — extracted from DocChatPanel
 *
 * Full-toolbar composer with Paperclip (attach), Smile (emoji insert),
 * AtSign (@mention) icons, auto-expanding textarea, reply preview bar,
 * and Cmd+Enter send.
 */

import { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { UserAvatar } from "../UserAvatar";
import { Send, Loader2, Paperclip, Smile, AtSign } from "lucide-react";
import { useAssignUserSuggestions } from "../../api/useDocAssignment";
import { useQueryClient } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { EmojiPicker } from "./EmojiPicker";
import { ReplyPreviewBar } from "./ReplyPreviewBar";
import { AttachmentPickerDialog } from "@/components/attachments";
import type { DocChatItem } from "@/lib/erp/docs";

type ChatComposerProps = {
  doctype: string;
  name: string;
  replyTo: DocChatItem | null;
  onClearReply: () => void;
  onSend: (params: { content: string; mentions?: string[]; reply_to?: string }) => void;
  isPending: boolean;
};

const LINE_HEIGHT = 20;
const MAX_LINES = 4;
const MAX_HEIGHT = LINE_HEIGHT * MAX_LINES + 16; // 4 lines + vertical padding

const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.userAgent);

export function ChatComposer({
  doctype,
  name,
  replyTo,
  onClearReply,
  onSend,
  isPending,
}: ChatComposerProps) {
  const [messageContent, setMessageContent] = useState("");
  const [isMentioning, setIsMentioning] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [selectedMentionIds, setSelectedMentionIds] = useState<string[]>([]);
  const [composerEmojiOpen, setComposerEmojiOpen] = useState(false);
  const [isAttachmentOpen, setIsAttachmentOpen] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const mentionDropdownRef = useRef<HTMLDivElement>(null);
  const cursorPosRef = useRef(0);
  const queryClient = useQueryClient();

  const { data: mentionSuggestions = [], isLoading: isLoadingMentions } = useAssignUserSuggestions(
    mentionQuery,
    {
      enabled: isMentioning,
      isOpen: isMentioning,
      minLength: 0,
    }
  );

  // Auto-expand textarea based on content
  const adjustHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = "auto";
    const clamped = Math.min(textarea.scrollHeight, MAX_HEIGHT);
    textarea.style.height = `${clamped}px`;
    textarea.style.overflowY = textarea.scrollHeight > MAX_HEIGHT ? "auto" : "hidden";
  }, []);

  useEffect(() => {
    adjustHeight();
  }, [messageContent, adjustHeight]);

  // Detect @mentions in message content
  useEffect(() => {
    if (!textareaRef.current) return;
    const textarea = textareaRef.current;
    const cursorPosition = textarea.selectionStart;
    const textBeforeCursor = messageContent.substring(0, cursorPosition);
    const mentionMatch = textBeforeCursor.match(/@([^\s@]*)$/);
    if (mentionMatch) {
      setIsMentioning(true);
      setMentionQuery(mentionMatch[1]);
    } else {
      setIsMentioning(false);
      setMentionQuery("");
    }
  }, [messageContent]);

  // Handle clicking outside mention dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        mentionDropdownRef.current &&
        !mentionDropdownRef.current.contains(event.target as Node) &&
        textareaRef.current &&
        !textareaRef.current.contains(event.target as Node)
      ) {
        setIsMentioning(false);
        setMentionQuery("");
      }
    };
    if (isMentioning) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isMentioning]);

  // Track cursor position for emoji/@ insertion
  const handleSelectionChange = useCallback(() => {
    if (textareaRef.current) {
      cursorPosRef.current = textareaRef.current.selectionStart;
    }
  }, []);

  const handleMentionSelect = (user: { value: string; label: string }) => {
    if (!textareaRef.current) return;
    const textarea = textareaRef.current;
    const cursorPosition = textarea.selectionStart;
    const textBeforeCursor = messageContent.substring(0, cursorPosition);
    const textAfterCursor = messageContent.substring(cursorPosition);
    const mentionMatch = textBeforeCursor.match(/@([^\s@]*)$/);
    if (mentionMatch) {
      const mentionStart = cursorPosition - mentionMatch[0].length;
      const visibleName = user.label.split(" (")[0];
      const newText =
        messageContent.substring(0, mentionStart) +
        `@${visibleName} ` +
        textAfterCursor;
      setMessageContent(newText);
      setSelectedMentionIds((prev) =>
        prev.includes(user.value) ? prev : [...prev, user.value]
      );
      setIsMentioning(false);
      setMentionQuery("");
      setTimeout(() => {
        if (textareaRef.current) {
          const newCursorPos = mentionStart + `@${visibleName} `.length;
          textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
          textareaRef.current.focus();
        }
      }, 0);
    }
  };

  const handleSend = () => {
    if (!messageContent.trim() || isPending) return;
    const params: { content: string; mentions?: string[]; reply_to?: string } = {
      content: messageContent.trim(),
    };
    if (selectedMentionIds.length > 0) {
      params.mentions = selectedMentionIds;
    }
    if (replyTo) {
      params.reply_to = replyTo.name;
    }
    onSend(params);
    setMessageContent("");
    setSelectedMentionIds([]);
    setIsMentioning(false);
    setMentionQuery("");
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      handleSend();
    }
  };

  // Emoji insert at cursor position
  const handleEmojiInsert = useCallback((emoji: string) => {
    const pos = cursorPosRef.current;
    const before = messageContent.substring(0, pos);
    const after = messageContent.substring(pos);
    const newText = before + emoji + after;
    setMessageContent(newText);
    const newPos = pos + emoji.length;
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.setSelectionRange(newPos, newPos);
        textareaRef.current.focus();
        cursorPosRef.current = newPos;
      }
    }, 0);
  }, [messageContent]);

  // AtSign insert at cursor position
  const handleAtSignClick = useCallback(() => {
    const pos = cursorPosRef.current;
    const before = messageContent.substring(0, pos);
    const after = messageContent.substring(pos);
    const newText = before + "@" + after;
    setMessageContent(newText);
    const newPos = pos + 1;
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.setSelectionRange(newPos, newPos);
        textareaRef.current.focus();
        cursorPosRef.current = newPos;
      }
    }, 0);
  }, [messageContent]);

  const handleAttachCompleted = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["doc-chat", doctype, name] });
  }, [queryClient, doctype, name]);

  const mentionDropdown = isMentioning ? (
    <div
      ref={mentionDropdownRef}
      className="absolute bottom-full left-0 right-0 mb-2 max-h-64 overflow-y-auto bg-background border border-border rounded shadow-lg z-50"
    >
      {isLoadingMentions ? (
        <div className="px-4 py-3 text-sm text-muted-foreground">Loading...</div>
      ) : mentionSuggestions.length > 0 ? (
        <div className="py-1">
          {mentionSuggestions.map((user) => (
            <button
              key={user.value}
              type="button"
              onClick={() => handleMentionSelect(user)}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted transition-colors text-left"
            >
              <UserAvatar
                imageUrl={user.avatar}
                fallbackText={user.label}
                size="sm"
                className="h-8 w-8"
              />
              <span className="text-sm font-medium">{user.label}</span>
            </button>
          ))}
        </div>
      ) : (
        <div className="px-4 py-3 text-sm text-muted-foreground">No users found</div>
      )}
    </div>
  ) : null;

  return (
    <div className="border-t border-border bg-background flex-shrink-0 relative">
      {replyTo && (
        <ReplyPreviewBar
          authorName={replyTo.user_full_name}
          content={replyTo.plain_text || replyTo.content}
          onDismiss={onClearReply}
        />
      )}
      <div className="p-4 md:p-6 relative">
        {mentionDropdown}
        <div className="flex items-end gap-2">
          {/* Toolbar icons */}
          <div className="flex items-center gap-0.5 pb-1.5">
            <button
              type="button"
              onClick={() => setIsAttachmentOpen(true)}
              className="h-8 w-8 flex items-center justify-center rounded-full hover:bg-muted transition-colors text-muted-foreground"
              aria-label="Attach file"
            >
              <Paperclip className="h-4 w-4" />
            </button>
            <EmojiPicker
              open={composerEmojiOpen}
              onOpenChange={setComposerEmojiOpen}
              onSelect={(emoji) => {
                handleEmojiInsert(emoji);
                setComposerEmojiOpen(false);
              }}
              onClose={() => setComposerEmojiOpen(false)}
            >
              <button
                type="button"
                className="h-8 w-8 flex items-center justify-center rounded-full hover:bg-muted transition-colors text-muted-foreground"
                aria-label="Insert emoji"
              >
                <Smile className="h-4 w-4" />
              </button>
            </EmojiPicker>
            <button
              type="button"
              onClick={handleAtSignClick}
              className="h-8 w-8 flex items-center justify-center rounded-full hover:bg-muted transition-colors text-muted-foreground"
              aria-label="Mention someone"
            >
              <AtSign className="h-4 w-4" />
            </button>
          </div>

          {/* Textarea */}
          <textarea
            ref={textareaRef}
            value={messageContent}
            onChange={(e) => setMessageContent(e.target.value)}
            onKeyDown={handleKeyDown}
            onSelect={handleSelectionChange}
            onClick={handleSelectionChange}
            placeholder="Type a message..."
            className={cn(
              "flex-1 resize-none rounded-lg border border-input bg-transparent px-3 py-2 text-sm",
              "placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
              "disabled:cursor-not-allowed disabled:opacity-50"
            )}
            style={{ minHeight: `${LINE_HEIGHT + 16}px`, maxHeight: `${MAX_HEIGHT}px`, overflowY: "hidden" }}
            disabled={isPending}
            rows={1}
          />

          {/* Send button + hint */}
          <div className="flex flex-col items-center gap-0.5 pb-0.5">
            <Button
              onClick={handleSend}
              disabled={!messageContent.trim() || isPending}
              className="h-10 w-10 rounded-full bg-primary hover:bg-primary/90 active:bg-primary/80 text-primary-foreground shadow-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
              size="icon"
            >
              {isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
            <span className="hidden md:block text-[10px] text-muted-foreground/50 select-none">
              {isMac ? "\u2318\u21B5" : "Ctrl\u21B5"}
            </span>
          </div>
        </div>
      </div>

      <AttachmentPickerDialog
        open={isAttachmentOpen}
        onOpenChange={setIsAttachmentOpen}
        context={{ doctype, name }}
        multiple
        onCompleted={handleAttachCompleted}
      />
    </div>
  );
}
